import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import AppContext from '../../contexts/AppContext'
import { fetchCharacterInventory, selectCharacter } from '../../pages/Characters/ViewInventory/inventorySlice'
import { fetchActiveTravel } from '../../features/travel/api'
import './CharNavbar.css'

const SECTIONS = [
  { path: 'ficha', label: 'Ficha', icon: '巻' },
  { path: 'tecnicas', label: 'Técnicas', icon: '術' },
  { path: 'equipo', label: 'Equipo', icon: '具' },
  { path: 'historia', label: 'Historia', icon: '史' },
  { path: 'progresion', label: 'Progresión', icon: '進' },
]

const TRAVEL_POLL_MS = 20000

function buildDisplayName(char = {}) {
  const givenName = String(char?.name || '').trim()
  const clan = String(char?.clan || '').trim()
  if (clan && givenName) return `${clan} ${givenName}`
  return givenName || 'Personaje'
}

function travelBadgeLabel(session) {
  if (!session) return null
  if (session.status === 'event_paused') return 'Viaje pausado'
  return `En viaje → ${session.destSlug || '…'}`
}

export default function CharNavbar() {
  const { pathname } = useLocation()
  const slug = pathname.match(/^\/personajes\/([^/]+)/)?.[1]
  const dispatch = useDispatch()
  const character = useSelector(selectCharacter)
  const { user } = useContext(AppContext)
  const [travelSession, setTravelSession] = useState(null)
  const [travelAdventure, setTravelAdventure] = useState(null)

  const isAcademy = Boolean(character?.academyMode)

  useEffect(() => {
    if (slug) {
      dispatch(fetchCharacterInventory(slug))
    }
  }, [slug, dispatch])

  useEffect(() => {
    const characterId = character?.id
    if (!characterId || !slug || isAcademy) {
      setTravelSession(null)
      setTravelAdventure(null)
      return undefined
    }

    let cancelled = false
    const poll = async () => {
      try {
        const { session, adventure } = await fetchActiveTravel(characterId)
        if (!cancelled) {
          setTravelSession(session || null)
          setTravelAdventure(adventure || null)
        }
      } catch {
        if (!cancelled) {
          setTravelSession(null)
          setTravelAdventure(null)
        }
      }
    }

    poll()
    const id = setInterval(poll, TRAVEL_POLL_MS)
    const onFocus = () => poll()
    window.addEventListener('focus', onFocus)
    return () => {
      cancelled = true
      clearInterval(id)
      window.removeEventListener('focus', onFocus)
    }
  }, [character?.id, slug, isAcademy])

  const displayName = useMemo(() => buildDisplayName(character), [character])
  const portraitSrc = useMemo(() => {
    const shuriken = '/assets/images/shuriken.png'
    const primary = String(character?.portrait || '').trim()
    if (primary) return primary
    if (slug) return `/assets/images/${slug}.png`
    return shuriken
  }, [character?.portrait, slug])

  const visibleSections = useMemo(() => {
    return SECTIONS.filter((s) => {
      if (isAcademy && s.path === 'progresion') return false
      return true
    }).map((s) => {
      if (isAcademy && s.path === 'equipo') {
        return { ...s, locked: true, lockTitle: 'Se desbloquea al graduarte' }
      }
      return s
    })
  }, [isAcademy])

  const badge = travelBadgeLabel(travelSession)
  const travelHref = travelAdventure?.slug
    ? `/aventura/${travelAdventure.slug}`
    : slug
      ? `/personajes/${slug}/ficha`
      : '/mis-personajes'

  if (!slug) return null

  const basePath = `/personajes/${slug}`

  return (
    <nav className="charNavbar" aria-label="Navegación de personaje">
      <div className="charNavbar__top">
        <div className="charNavbar__identity">
          <Link to="/mis-personajes" className="charNavbar__back" title="Volver a mis shinobis">
            <span className="charNavbar__back-icon" aria-hidden="true">
              ‹
            </span>
            <span className="charNavbar__back-label">Mis shinobis</span>
          </Link>

          <div className="charNavbar__nameplate" title={displayName}>
            <img
              src={portraitSrc}
              alt=""
              className="charNavbar__avatar"
              onError={(e) => {
                e.currentTarget.src = '/assets/images/shuriken.png'
              }}
            />
            <span className="charNavbar__name">{displayName}</span>
          </div>

          {badge ? (
            <Link
              to={travelHref}
              className={`charNavbar__travel-badge${
                travelSession?.status === 'event_paused' ? ' charNavbar__travel-badge--paused' : ''
              }`}
              title={travelAdventure?.title || 'Ver crónica de viaje'}
            >
              {badge}
            </Link>
          ) : null}
        </div>

        <div className="charNavbar__utils">
          <span className="charNavbar__util charNavbar__util--disabled" title="Próximamente">
            Diario de Nikki
          </span>
          <span className="charNavbar__util charNavbar__util--disabled" title="Próximamente">
            Enciclopedia
          </span>
          <Link to="/mapa" className="charNavbar__util">
            Mapa
          </Link>
          {user && (
            <Link to="/" className="charNavbar__util charNavbar__util--subtle">
              Inicio
            </Link>
          )}
        </div>
      </div>

      <div className="charNavbar__sections-wrap">
        <div className="charNavbar__sections" role="tablist" aria-label="Secciones de personaje">
          {visibleSections.map(({ path, label, icon, soon, locked, lockTitle }) => {
            if (locked) {
              return (
                <span
                  key={path}
                  className="charNavbar__tab charNavbar__tab--locked"
                  role="tab"
                  aria-disabled="true"
                  title={lockTitle || 'Bloqueado'}
                >
                  <span className="charNavbar__tab-glyph" aria-hidden="true">
                    {icon}
                  </span>
                  <span className="charNavbar__tab-label">{label}</span>
                </span>
              )
            }
            return (
              <NavLink
                key={path}
                to={`${basePath}/${path}`}
                className={({ isActive }) =>
                  `charNavbar__tab${isActive ? ' active' : ''}${soon ? ' charNavbar__tab--soon' : ''}`
                }
                role="tab"
                title={soon ? 'Próximamente' : undefined}
              >
                <span className="charNavbar__tab-glyph" aria-hidden="true">
                  {icon}
                </span>
                <span className="charNavbar__tab-label">{label}</span>
              </NavLink>
            )
          })}
        </div>
      </div>
    </nav>
  )
}
